import { type ShiftAuction } from "@clipboard-health/contract-worker-app-bff";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { useWorkerCustomToBaseQualificationNameMap } from "@src/appV2/Qualifications/useWorkerCustomToBaseQualificationNameMap";
import { useIsShiftBlocksEnabled } from "@src/appV2/redesign/Shift/Block/useIsShiftBlocksEnabled";
import { useMemo } from "react";

import { useDefinedWorker } from "../../../Worker/useDefinedWorker";
import { useShiftBundles } from "../../Shift/Bundle/useShiftBundles";
import { findAndPartitionEligibleWorkplaces, mergeBundles } from "../../Shift/Bundle/utils";
import { useShiftDoubles } from "../../Shift/Double/useShiftDoubles";
import { useShiftDoublesEnabled } from "../../Shift/Double/useShiftDoublesEnabled";
import { usePreShiftOrientationsByShiftId } from "../../Shift/PreShiftOrientation/usePreShiftOrientationsByShiftId";
import { useWorkplacesWithOrientationCredentials } from "../../Shift/PreShiftOrientation/useWorkplacesWithOrientationCredentials";
import { isPriorityShift } from "../../Shift/Priority/isPriorityShift";
import { useGetWorkerShifts } from "../../WorkerShifts/api/useGetWorkerShifts";
import { useGetMultiLicenseAggregatedBlocks } from "../Blocks/hooks/useGetMultiLicenseAggregatedBlocks";
import { filterOpenShifts } from "../Filters/filterOpenShifts";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { useUrgentShiftsData } from "../UrgentShifts/useUrgentShiftsData";
import { useListViewOpenShiftsData } from "./useListViewOpenShiftsData";
import { useShiftListInjector } from "./useShiftListInjector";

export function useFilteredListViewOpenShiftsData() {
  const isShiftBlocksEnabled = useIsShiftBlocksEnabled();
  const isShiftDoublesEnabled = useShiftDoublesEnabled();

  const {
    distance,
    // Filtering by timeslot happens at the list rendering level
    // because each date can have a different set of timeslots
    shiftTimeSlots: timeSlotFilters,
    includeWindowedOutShifts,
    ...localUserFilters
  } = useShiftDiscoveryUserFiltersContext();

  const { filters: shiftBlockFilters } = useWorkerPreferences();

  const {
    shifts = [],
    workplacesMap,
    rateNegotiationsMap = new Map(),
    offersMap,
    shiftAuctionsMap = new Map<string, ShiftAuction>(),
    ...openShiftsDataResult
  } = useListViewOpenShiftsData({
    distance,
    includeWindowedOutShifts,
    isShiftDoublesEnabled,
  });

  const {
    workplacesWithOrientationCredentials,
    isLoading: isOrientationCredentialsLoading,
    isError: isOrientationCredentialsError,
  } = useWorkplacesWithOrientationCredentials();
  const worker = useDefinedWorker();
  const {
    shifts: bookedShifts,
    isLoading: isLoadingBookedShifts,
    isErrored: isErroredBookedShifts,
  } = useGetWorkerShifts({ worker });

  const { regularBundleWorkplaceIds, orientationBundleWorkplaceIds } = useMemo(() => {
    if (
      isOrientationCredentialsLoading ||
      isLoadingBookedShifts ||
      isErroredBookedShifts ||
      workplacesMap.size === 0
    ) {
      return {
        regularBundleWorkplaceIds: new Set<string>(),
        orientationBundleWorkplaceIds: new Set<string>(),
      };
    }

    const formattedBookedShifts = bookedShifts.map((shift) => ({
      start: shift.start,
      end: shift.end,
      workplaceId: shift.facility.userId ?? "",
      qualification: shift.agentReq,
    }));

    return findAndPartitionEligibleWorkplaces({
      workplacesMap,
      workplacesWithOrientationCredentials,
      bookedShifts: formattedBookedShifts,
      // If there is an error loading orientation credentials, we don't want that
      // to block us from forming regular bundles
      skipOrientationWorkplaces: isOrientationCredentialsError,
    });
  }, [
    workplacesMap,
    workplacesWithOrientationCredentials,
    bookedShifts,
    isOrientationCredentialsLoading,
    isOrientationCredentialsError,
    isLoadingBookedShifts,
    isErroredBookedShifts,
  ]);

  const regularBundlesByShiftId = useShiftBundles(shifts, {
    eligibleWorkplaceIds: regularBundleWorkplaceIds,
  });
  const orientationBundlesByShiftId = useShiftBundles(shifts, {
    eligibleWorkplaceIds: orientationBundleWorkplaceIds,
    isOrientationBundle: true,
  });

  const bundlesByShiftId = mergeBundles([regularBundlesByShiftId, orientationBundlesByShiftId]);

  const doublesByShiftId = useShiftDoubles(shifts);
  const preShiftOrientationsByShiftId = usePreShiftOrientationsByShiftId(shifts, workplacesMap);
  // The open shifts data doesn't include all urgent shifts, so we need to fetch them separately
  const { shifts: urgentShifts } = useUrgentShiftsData();

  const { data: blockShifts } = useGetMultiLicenseAggregatedBlocks(
    {
      maxDistanceMiles: shiftBlockFilters.distance,
    },
    {
      enabled: isShiftBlocksEnabled,
    }
  );

  const priorityShifts = shifts.filter((shift) =>
    isPriorityShift({
      priorityTill: shift.attributes.priorityTill,
      window: shift.attributes.window,
    })
  );

  const customToBaseQualificationNameMap = useWorkerCustomToBaseQualificationNameMap();

  const filteredOpenShifts = filterOpenShifts(
    { shifts, workplacesMap },
    localUserFilters,
    customToBaseQualificationNameMap
  );

  // shifts post filtering injection.  I.E. in-app ads, the home health banner (once we migrate that), etc...
  const postInjectionShifts = useShiftListInjector(filteredOpenShifts);

  return {
    data: {
      filteredOpenShifts: postInjectionShifts,
      unfilteredOpenShifts: shifts,
      workplacesMap,
      rateNegotiationsMap,
      shiftAuctionsMap,
      offersMap,
      urgentShiftsCount: urgentShifts.length,
      priorityShiftsCount: priorityShifts.length,
      blockShiftsCount: blockShifts?.data.length ?? 0,
      timeSlotFilters,
      bundlesByShiftId,
      doublesByShiftId,
      preShiftOrientationsByShiftId,
    },
    ...openShiftsDataResult,
  };
}
